import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Badge, Button, Alert, Modal } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { userService, departmentService } from '../../services/api';
import './EmployeeDetails.css';

const EmployeeDetails = () => {
  const { currentUser, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add state for password reset functionality
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  // Add state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Add state for edit employee
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    role: '',
    department_id: ''
  });
  const [editSuccess, setEditSuccess] = useState(false);
  const [editError, setEditError] = useState('');

  // Add state for view details
  const [showViewModal, setShowViewModal] = useState(false);

  // Add state for success messages
  const [success, setSuccess] = useState('');

  // Add state for new employee
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'employee',
    department_id: ''
  });
  const [addSuccess, setAddSuccess] = useState(false);
  const [addError, setAddError] = useState('');

  // Create a function to fetch employee data that can be called from multiple places
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      // Fetch departments
      const departmentsResponse = await departmentService.getAllDepartments();
      if (departmentsResponse.data && departmentsResponse.data.success) {
        setDepartments(departmentsResponse.data.data || []);
      } else {
        setDepartments([]);
      }

      // Fetch all users/employees
      const usersResponse = await userService.getAllUsers();
      if (usersResponse.data && usersResponse.data.success) {
        // Filter only employees
        const employeesList = (usersResponse.data.data || []).filter(user => 
          user.role === 'employee' || user.role === 'user'
        );
        setEmployees(employeesList);
        setFilteredEmployees(employeesList);
      } else {
        setEmployees([]);
        setFilteredEmployees([]);
      }
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load employee data. Please try again.');
      setDepartments([]);
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchEmployeeData();
  }, []);

  // Apply filters when department filter or search term changes
  useEffect(() => {
    if (!Array.isArray(employees)) {
      setFilteredEmployees([]);
      return;
    }

    let result = [...employees];
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      result = result.filter(emp => emp.department_id?.toString() === departmentFilter);
    }
    
    // Apply search term
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(emp => 
        emp.username?.toLowerCase().includes(term) || 
        emp.email?.toLowerCase().includes(term)
      );
    }
    
    setFilteredEmployees(result);
  }, [employees, departmentFilter, searchTerm]);

  const handleDepartmentChange = (e) => {
    setDepartmentFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Add functions for password reset
  const handleResetPassword = (employee) => {
    setSelectedEmployee(employee);
    setNewPassword('');
    setResetSuccess(false);
    setResetError('');
    setShowPasswordModal(true);
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const submitPasswordReset = async () => {
    if (!newPassword || newPassword.length < 3) {
      setResetError('Password must be at least 3 characters long');
      return;
    }

    try {
      // Call API to update user password
      const response = await userService.updateUser(selectedEmployee.id, {
        password: newPassword
      });

      if (response.data && response.data.success) {
        setResetSuccess(true);
        setResetError('');
        
        // Show success message
        const successMessage = `Password updated successfully for ${selectedEmployee.username}`;
        setError(''); // Clear any existing errors
        setSuccess(successMessage);
        
        // Close modal after a short delay
        setTimeout(() => {
          setShowPasswordModal(false);
          setSuccess(''); // Clear success message after modal closes
        }, 1500);
        
        // Reset state
        setNewPassword('');
        setSelectedEmployee(null);
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setResetError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    }
  };

  // Handler for view employee details
  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowViewModal(true);
  };

  // Handlers for edit employee
  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setEditForm({
      username: employee.username,
      email: employee.email,
      role: employee.role || 'employee',
      department_id: employee.department_id || ''
    });
    setEditSuccess(false);
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitEditForm = async () => {
    try {
      // Basic validation
      if (!editForm.username.trim() || !editForm.email.trim()) {
        setEditError('Username and email are required');
        return;
      }

      // Call API to update employee details
      const response = await userService.updateUser(selectedEmployee.id, editForm);
      
      if (response.data && response.data.success) {
        setEditSuccess(true);
        setEditError('');
        
        // Show success message with details of what was updated
        const successMessage = response.data.message || 'Employee details updated successfully';
        setError(''); // Clear any existing errors
        setSuccess(successMessage);
        
        // Close modal after a short delay
        setTimeout(() => {
          setShowEditModal(false);
          setSuccess(''); // Clear success message after modal closes
        }, 1500);
        
        // Reset state
        setSelectedEmployee(null);
        setEditForm({
          username: '',
          email: '',
          role: '',
          department_id: ''
        });
        
        // Refresh the data
        await fetchEmployeeData();
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      setEditError(err.response?.data?.message || 'Failed to update employee. Please try again.');
    }
  };

  // Handlers for delete employee
  const handleDeleteEmployee = (employee) => {
    setSelectedEmployee(employee);
    setDeleteSuccess(false);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDeleteEmployee = async () => {
    try {
      // Show deleting in progress
      setDeleteError('');
      
      // Call API to delete employee
      const response = await userService.deleteUser(selectedEmployee.id);
      console.log('Delete employee response:', response);
      
      // Show success message for a short time
      setDeleteSuccess(true);
      
      // Wait a moment to show success message
      setTimeout(async () => {
        // Close modal
        setShowDeleteModal(false);
        
        // Reset state
        setDeleteSuccess(false);
        setSelectedEmployee(null);
        
        // Refresh the data
        await fetchEmployeeData();
      }, 800);
      
    } catch (err) {
      console.error('Error deleting employee:', err);
      // Extract more specific error from response if available
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to delete employee. Please try again.';
      
      setDeleteError(errorMessage);
    }
  };

  // Add handlers for new employee
  const handleAddEmployee = () => {
    setAddForm({
      username: '',
      email: '',
      password: '',
      role: 'employee',
      department_id: ''
    });
    setAddSuccess(false);
    setAddError('');
    setShowAddModal(true);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitAddForm = async () => {
    try {
      // Basic validation
      if (!addForm.username.trim() || !addForm.email.trim() || !addForm.password.trim()) {
        setAddError('Username, email and password are required');
        return;
      }

      if (addForm.password.length < 3) {
        setAddError('Password must be at least 3 characters long');
        return;
      }

      // Call API to create new employee
      const response = await userService.registerUser(addForm);
      
      if (response.data && response.data.success) {
        setAddSuccess(true);
        setAddError('');
        
        // Show success message
        const successMessage = `New employee ${addForm.username} created successfully`;
        setError(''); // Clear any existing errors
        setSuccess(successMessage);
        
        // Close modal after a short delay
        setTimeout(() => {
          setShowAddModal(false);
          setSuccess(''); // Clear success message after modal closes
        }, 1500);
        
        // Reset form
        setAddForm({
          username: '',
          email: '',
          password: '',
          role: 'employee',
          department_id: ''
        });
        
        // Refresh employee list
        await fetchEmployeeData();
      }
    } catch (err) {
      console.error('Error creating employee:', err);
      setAddError(err.response?.data?.message || 'Failed to create employee. Please try again.');
    }
  };

  if (loading) {
    return <div className="text-center p-5">Loading employee details...</div>;
  }

  if (!isAdmin) {
    return (
      <Container>
        <Alert variant="danger" className="mt-4">
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        <i className="fas fa-id-card me-2"></i>Employee Details
      </h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="shadow-sm mb-4 border-0">
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Filter Employees</h5>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleAddEmployee}
            >
              <i className="fas fa-plus me-1"></i> Add New Employee
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Filter by Department</Form.Label>
                <Form.Select 
                  value={departmentFilter}
                  onChange={handleDepartmentChange}
                  disabled={!Array.isArray(departments) || departments.length === 0}
                >
                  <option value="all">All Departments</option>
                  {Array.isArray(departments) && departments.map(dept => (
                    <option key={dept.id} value={dept.id.toString()}>
                      {dept.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Search Employees</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading employee data...</p>
        </div>
      ) : Array.isArray(filteredEmployees) && filteredEmployees.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Employee List</h5>
              <span className="badge bg-primary">
                {filteredEmployees.length} employees
              </span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(employee => (
                    <tr key={employee.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="employee-avatar me-3">
                            {employee.username.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <h6 className="mb-0">{employee.username}</h6>
                          </div>
                        </div>
                      </td>
                      <td>{employee.email}</td>
                      <td>
                        <Badge bg="info" className="department-badge">
                          {employee.department_name || 'Unassigned'}
                        </Badge>
                      </td>
                      <td>
                        <Badge 
                          bg={employee.role === 'admin' ? 'danger' : 
                              employee.role === 'manager' ? 'warning' : 'success'}
                        >
                          {employee.role}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-2"
                          title="View Details"
                          onClick={() => handleViewDetails(employee)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          size="sm" 
                          className="me-2"
                          title="Edit Employee"
                          onClick={() => handleEditEmployee(employee)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          className="me-2"
                          title="Reset Password"
                          onClick={() => handleResetPassword(employee)}
                        >
                          <i className="fas fa-key"></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          title="Delete Employee" 
                          onClick={() => handleDeleteEmployee(employee)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info" className="text-center">
          No employees found. {searchTerm || departmentFilter !== 'all' ? 'Try adjusting your filters.' : ''}
        </Alert>
      )}

      {/* Password Reset Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetSuccess ? (
            <Alert variant="success">
              Password has been reset successfully!
            </Alert>
          ) : (
            <>
              {resetError && <Alert variant="danger">{resetError}</Alert>}
              <p>
                Resetting password for: <strong>{selectedEmployee?.username}</strong>
              </p>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={handlePasswordChange}
                />
                <Form.Text className="text-muted">
                  Password must be at least 6 characters long.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        {!resetSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitPasswordReset}>
              Reset Password
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* View Employee Details Modal */}
      <Modal 
        show={showViewModal} 
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Employee Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <div>
              <Row className="mb-3">
                <Col md={3}>
                  <div className="text-center">
                    <div className="employee-avatar mx-auto" style={{ width: '80px', height: '80px', fontSize: '1.5rem' }}>
                      {selectedEmployee.username.substring(0, 2).toUpperCase()}
                    </div>
                  </div>
                </Col>
                <Col md={9}>
                  <h4>{selectedEmployee.username}</h4>
                  <p className="text-muted mb-1">{selectedEmployee.email}</p>
                  <div>
                    <Badge 
                      bg={selectedEmployee.role === 'admin' ? 'danger' : 
                          selectedEmployee.role === 'manager' ? 'warning' : 'success'}
                      className="me-2"
                    >
                      {selectedEmployee.role}
                    </Badge>
                    <Badge bg="info">
                      {selectedEmployee.department_name || 'Unassigned'}
                    </Badge>
                  </div>
                </Col>
              </Row>
              
              <hr />
              
              <h5 className="mb-3">Account Information</h5>
              <Row>
                <Col md={4} className="mb-3">
                  <div className="text-muted small">User ID</div>
                  <div>{selectedEmployee.id}</div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="text-muted small">Created</div>
                  <div>{selectedEmployee.created_at ? new Date(selectedEmployee.created_at).toLocaleDateString() : 'N/A'}</div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="text-muted small">Last Updated</div>
                  <div>{selectedEmployee.updated_at ? new Date(selectedEmployee.updated_at).toLocaleDateString() : 'N/A'}</div>
                </Col>
              </Row>
              
              <h5 className="mb-3">Contact & Assignment</h5>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Email Address</div>
                  <div>{selectedEmployee.email}</div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Department</div>
                  <div>{selectedEmployee.department_name || 'Unassigned'}</div>
                </Col>
              </Row>
              
              <h5 className="mb-3">Account Actions</h5>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditEmployee(selectedEmployee);
                  }}
                >
                  <i className="fas fa-edit me-1"></i> Edit
                </Button>
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setShowViewModal(false);
                    handleResetPassword(selectedEmployee);
                  }}
                >
                  <i className="fas fa-key me-1"></i> Reset Password
                </Button>
                <Button 
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    setShowViewModal(false);
                    handleDeleteEmployee(selectedEmployee);
                  }}
                >
                  <i className="fas fa-trash me-1"></i> Delete
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Employee Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editSuccess ? (
            <Alert variant="success">
              Employee details updated successfully!
            </Alert>
          ) : (
            <>
              {editError && <Alert variant="danger">{editError}</Alert>}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    name="username"
                    value={editForm.username}
                    onChange={handleEditFormChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditFormChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={editForm.role}
                    onChange={handleEditFormChange}
                  >
                    <option value="employee">Employee</option>
                    {isAdmin && <option value="admin">Admin</option>}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    name="department_id"
                    value={editForm.department_id || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">-- None --</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id.toString()}>
                        {dept.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        {!editSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitEditForm}>
              Save Changes
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Delete Employee Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteSuccess ? (
            <Alert variant="success">
              Employee deleted successfully!
            </Alert>
          ) : (
            <>
              {deleteError && <Alert variant="danger">{deleteError}</Alert>}
              <p>
                Are you sure you want to delete the employee: <strong>{selectedEmployee?.username}</strong>?
              </p>
              <p className="text-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                This action cannot be undone. All data associated with this employee will be permanently deleted.
              </p>
            </>
          )}
        </Modal.Body>
        {!deleteSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDeleteEmployee}>
              Delete Employee
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Add Employee Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addSuccess ? (
            <Alert variant="success">
              Employee created successfully!
            </Alert>
          ) : (
            <>
              {addError && <Alert variant="danger">{addError}</Alert>}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    name="username"
                    value={addForm.username}
                    onChange={handleAddFormChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={addForm.email}
                    onChange={handleAddFormChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter password"
                    name="password"
                    value={addForm.password}
                    onChange={handleAddFormChange}
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 6 characters long.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    name="department_id"
                    value={addForm.department_id}
                    onChange={handleAddFormChange}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={addForm.role}
                    onChange={handleAddFormChange}
                  >
                    <option value="employee">Employee</option>
                    {isAdmin && <option value="admin">Admin</option>}
                  </Form.Select>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        {!addSuccess && (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitAddForm}>
              Add Employee
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </Container>
  );
};

export default EmployeeDetails; 